import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import PageHero from "@/components/feature/PageHero";
import CtaBanner from "@/components/feature/CtaBanner";
import Seo from "@/components/feature/Seo";
import JsonLd from "@/components/feature/JsonLd";
import FaqAccordion from "@/components/feature/FaqAccordion";
import BlogImage from "@/components/feature/BlogImage";
import Button from "@/components/base/Button";
import { blogPosts, getPostBySlug, getRelatedPosts } from "@/data/blogs";
import { services } from "@/data/services";
import { formatDate } from "@/lib/format";

const SITE_URL = "https://orthospine.care";

export default function BlogDetail() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;

  if (!post) {
    return (
      <Layout>
        <Seo
          title="Article Not Found | Ortho Spine Care Blog"
          description="The health article you are looking for could not be found. Browse all orthopaedic and spine health articles from Ortho Spine Care, Trichy."
          path="/blog"
        />
        <section className="bg-background-50 py-16 md:py-24">
          <div className="mx-auto max-w-3xl px-4 text-center md:px-6">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 text-accent-700">
              <i className="ri-article-line text-2xl" aria-hidden="true"></i>
            </span>
            <h1 className="mt-6 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
              We couldn&apos;t find that article
            </h1>
            <p className="mx-auto mt-3 max-w-lg font-body text-sm leading-relaxed text-foreground-600 md:text-base">
              The article may have been moved or the link may be incorrect. All of our health information is
              available on the blog.
            </p>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <Button to="/blog" icon="ri-article-line">
                Back to the Blog
              </Button>
              <Button to="/appointment" variant="outline" icon="ri-calendar-check-line">
                Book Appointment
              </Button>
            </div>

            <div className="mt-10 rounded-lg border border-background-200 bg-background-100/60 p-6 text-left">
              <h2 className="font-heading text-base font-semibold text-foreground-950">Latest articles</h2>
              <ul className="mt-4 space-y-3">
                {blogPosts.slice(0, 5).map((recent) => (
                  <li key={recent.slug}>
                    <Link
                      to={`/blog/${recent.slug}`}
                      className="flex items-start gap-3 font-body text-sm text-foreground-700 transition-colors hover:text-primary-700"
                    >
                      <i className="ri-arrow-right-s-line mt-0.5 text-primary-600" aria-hidden="true"></i>
                      {recent.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  const relatedServices = services.filter((service) => post.relatedServices.includes(service.slug));
  const relatedPosts = getRelatedPosts(post, 3);

  return (
    <Layout>
      <Seo
        title={post.metaTitle}
        description={post.metaDescription}
        path={`/blog/${post.slug}`}
        image={post.featuredImage}
        type="article"
      />
      <JsonLd
        id="ld-blog"
        data={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "BlogPosting",
              headline: post.title,
              description: post.metaDescription,
              image: post.featuredImage,
              url: `${SITE_URL}/blog/${post.slug}`,
              datePublished: post.publishedDate,
              dateModified: post.updatedDate,
              keywords: post.tags.join(", "),
              articleSection: post.category,
              author: { "@type": "Person", name: post.author },
              publisher: { "@type": "Organization", name: "Ortho Spine Care", url: SITE_URL },
              mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
                { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: post.title,
                  item: `${SITE_URL}/blog/${post.slug}`,
                },
              ],
            },
          ],
        }}
      />

      <PageHero
        title={post.title}
        subtitle={post.excerpt}
        crumbs={[
          { label: "Home", to: "/" },
          { label: "Blog", to: "/blog" },
          { label: post.category, to: "/blog" },
        ]}
      />

      <section className="bg-background-50 py-14 md:py-20">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-12 lg:gap-12">
          <article className="lg:col-span-8">
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 font-label text-sm font-semibold text-primary-700 hover:text-primary-800"
            >
              <i className="ri-arrow-left-line" aria-hidden="true"></i>
              Back to all articles
            </Link>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 font-label text-xs text-foreground-500">
              <span className="inline-flex items-center rounded-full bg-accent-100 px-2.5 py-1 font-semibold text-accent-800">
                {post.category}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line" aria-hidden="true"></i>
                {post.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" aria-hidden="true"></i>
                Published {formatDate(post.publishedDate)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-refresh-line" aria-hidden="true"></i>
                Updated {formatDate(post.updatedDate)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" aria-hidden="true"></i>
                {post.readTime}
              </span>
            </div>

            <div className="mt-6 h-[260px] w-full overflow-hidden rounded-lg border border-background-200 md:h-[400px]">
              <BlogImage
                src={post.featuredImage}
                alt={post.imageAlt}
                title={`${post.title} — Ortho Spine Care`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="mt-8 space-y-8">
              {post.content.map((section, index) => (
                <section key={`${index}-${section.heading}`}>
                  <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                    {section.heading}
                  </h2>
                  {section.paragraphs?.map((paragraph) => (
                    <p
                      key={paragraph}
                      className="mt-3 font-body text-sm leading-relaxed text-foreground-700 md:text-base"
                    >
                      {paragraph}
                    </p>
                  ))}
                  {section.bullets ? (
                    <ul className="mt-4 space-y-3">
                      {section.bullets.map((bullet) => (
                        <li
                          key={bullet}
                          className="flex items-start gap-3 font-body text-sm leading-relaxed text-foreground-700 md:text-base"
                        >
                          <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-accent-100 text-accent-700">
                            <i className="ri-check-line text-xs" aria-hidden="true"></i>
                          </span>
                          {bullet}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </section>
              ))}
            </div>

            <section className="mt-10">
              <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                Frequently asked questions
              </h2>
              <div className="mt-4">
                <FaqAccordion items={post.faqs} idPrefix={`blog-${post.slug}`} />
              </div>
            </section>

            {post.tags.length ? (
              <div className="mt-8 flex flex-wrap items-center gap-2">
                <span className="font-label text-xs font-semibold uppercase tracking-wide text-foreground-500">
                  Tags
                </span>
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-background-300 bg-background-100 px-3 py-1 font-label text-xs text-foreground-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            ) : null}

            <div className="mt-8 rounded-lg border border-background-200 bg-secondary-50 p-6">
              <p className="font-body text-xs leading-relaxed text-secondary-900">
                <strong>Medical disclaimer:</strong> This article is for general health information only and does not
                replace consultation, diagnosis or treatment by a qualified healthcare professional. Always seek the
                advice of your physician regarding any medical condition.
              </p>
            </div>
          </article>

          <aside className="lg:col-span-4">
            <div className="rounded-lg border border-background-200 bg-primary-950 p-6">
              <h2 className="font-heading text-base font-semibold text-background-50">Need advice?</h2>
              <p className="mt-2 font-body text-sm leading-relaxed text-background-200">
                Book a consultation with our orthopaedic and spine specialist.
              </p>
              <div className="mt-4">
                <Button to="/appointment" variant="light" icon="ri-calendar-check-line" fullWidth>
                  Book Appointment
                </Button>
              </div>
            </div>

            {relatedServices.length ? (
              <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-6">
                <h2 className="font-heading text-base font-semibold text-foreground-950">Related services</h2>
                <ul className="mt-4 space-y-3">
                  {relatedServices.map((service) => (
                    <li key={service.slug}>
                      <Link
                        to={`/services/${service.slug}`}
                        className="flex items-center justify-between gap-3 font-body text-sm text-foreground-700 transition-colors hover:text-primary-700"
                      >
                        {service.title}
                        <i className="ri-arrow-right-line text-primary-600" aria-hidden="true"></i>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {relatedPosts.length ? (
              <div className="mt-5 rounded-lg border border-background-200 bg-background-100/60 p-6">
                <h2 className="font-heading text-base font-semibold text-foreground-950">Related articles</h2>
                <ul className="mt-4 space-y-4">
                  {relatedPosts.map((related) => (
                    <li key={related.slug}>
                      <Link to={`/blog/${related.slug}`} className="group flex gap-3">
                        <span className="h-14 w-16 flex-none overflow-hidden rounded-md bg-background-200">
                          <BlogImage
                            src={related.featuredImage}
                            alt={related.imageAlt}
                            title={`${related.title} — Ortho Spine Care`}
                            className="h-full w-full object-cover object-top"
                          />
                        </span>
                        <span className="font-body text-sm leading-snug text-foreground-700 transition-colors group-hover:text-primary-700">
                          {related.title}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </aside>
        </div>
      </section>

      <CtaBanner />
    </Layout>
  );
}